/**
 * ThemeLangFab — Floating EN/ES toggle that auto-appears on theme preview
 * and tenant microsite routes.
 *
 * Shows when the URL matches any of:
 *   • /preview-{theme}/...
 *   • /s/{subdomain}/...
 *   • Any route on a real tenant subdomain (isSubdomain)
 *
 * Clicking toggles /es on/off and persists the choice (via rememberLangChoice).
 * Uses URL-based detection (isEsLocale) so it works even when not wrapped in
 * a LanguageProvider — important for /preview-* demo routes.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Languages } from "lucide-react";
import { isEsLocale } from "../themes/_i18n/themeI18n";
import { useThemeBaseRoot } from "../hooks/useThemeBase";
import { rememberLangChoice } from "./public/PublicLangToggle";

const THEME_ROUTE_RE = /^\/(preview-[a-z-]+|s\/[^/]+)(\/|$)/;

export default function ThemeLangFab() {
  const location = useLocation();
  const navigate = useNavigate();
  const base = useThemeBaseRoot();

  // Only render on theme preview or tenant site routes
  const onThemeRoute = THEME_ROUTE_RE.test(location.pathname);
  if (!onThemeRoute) return null;

  const isEs = isEsLocale();

  const swap = () => {
    const afterBase = location.pathname.slice(base.length);
    let rest = afterBase;
    if (rest === "/es" || rest.startsWith("/es/")) {
      rest = rest.slice(3) || "/";
    }
    const target = isEs
      ? `${base}${rest}${location.search}`
      : `${base}/es${rest === "/" ? "" : rest}${location.search}`;
    rememberLangChoice(isEs ? "en" : "es");
    navigate(target);
  };

  const label = isEs ? "EN" : "ES";
  const title = isEs ? "View in English" : "Ver en Español";

  return (
    <button
      type="button"
      onClick={swap}
      title={title}
      data-testid="theme-lang-fab"
      className="fixed bottom-5 right-5 z-[9999] inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider border border-black/15 bg-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
      style={{ color: "#111" }}
    >
      <Languages className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}
